// Walking a trail — the handful of questions both views of it ask.
//
// The trail comes back as a tree, and it is drawn two ways: as the process
// (TrailDesign) and as the hierarchy (TrailTree). Which record is the anchor,
// what identifies a node, which branches exist — one answer each, here, so the
// two views cannot disagree about the same chain.

import type { TrailNode } from './contract'

/** A node's identity within one trail. A record can legitimately appear under
 *  two parents; the key is the record, not the position. */
export function nodeKey(node: TrailNode): string {
  return `${node.obj_name}:${node.txn_id || ''}`
}

/** The record the trail was opened FOR. */
export function findAnchor(nodes: TrailNode[]): TrailNode | null {
  for (const n of nodes) {
    if (n.is_anchor) return n
    const hit = findAnchor(n.children || [])
    if (hit) return hit
  }
  return null
}

/** Every branch key in a tree — what "collapse all" needs. */
export function branchKeys(nodes: TrailNode[], out: string[] = []): string[] {
  for (const n of nodes) {
    if ((n.children || []).length > 0) out.push(nodeKey(n))
    branchKeys(n.children || [], out)
  }
  return out
}

/** The path of keys from the roots down to the anchor, so a trail opens with
 *  the record the person came from already in view. */
export function anchorPath(nodes: TrailNode[], trail: string[] = []): string[] {
  for (const n of nodes) {
    const here = [...trail, nodeKey(n)]
    if (n.is_anchor) return here
    const deeper = anchorPath(n.children || [], here)
    if (deeper.length) return deeper
  }
  return []
}

/** Every parent → child pair in the chain, by node key.
 *
 *  The diagram's arrows are drawn from THESE rather than from the flow's own
 *  step-to-step arrows: a record raised a record, and a reader looking at a box
 *  with five plans in it needs to know which one. */
export function recordLinks(
  nodes: TrailNode[], out: Array<{ from: string; to: string }> = [],
): Array<{ from: string; to: string }> {
  for (const n of nodes) {
    for (const kid of n.children || []) out.push({ from: nodeKey(n), to: nodeKey(kid) })
    recordLinks(n.children || [], out)
  }
  return out
}

/** How many records a shut branch is holding back — the count a collapsed node
 *  carries, so nobody has to open one to find out whether anything is there. */
export function descendantCount(node: TrailNode): number {
  return (node.children || []).reduce((n, k) => n + 1 + descendantCount(k), 0)
}

/** Every node that has a body to open — what "expand all" acts on. */
export function bodyNodes(nodes: TrailNode[], out: TrailNode[] = []): TrailNode[] {
  for (const n of nodes) {
    if (n.txn_id && n.card?.has_body) out.push(n)
    bodyNodes(n.children || [], out)
  }
  return out
}
